/**
 * Recommendations source for the cart drawer.
 *
 * Nosto supplies the DATA and we render the carousel ourselves, so styling and markup stay in
 * the theme rather than inside a Nosto template.
 *
 * The request is `createRecommendationRequest().setResponseMode('JSON_ORIGINAL')`, which returns
 * raw product objects instead of rendered HTML. Verified against production:
 *
 *   recommendations['cartpage-nosto-1'] = {
 *     result_id, title, result_type, div_id, element, source_product_ids,
 *     products: [{ product_id, name, handle, url, price, price_text, list_price,
 *                  list_price_text, image_url, thumb_url, rating_value, brand,
 *                  skus: [{ id, name, available, image_url, price, custom_fields }] }]
 *   }
 *
 * A SKU's `custom_fields` carries `cql-swatch_image` and `Color` — the same values the theme
 * reads from `variant.metafields.cql.swatch_image`, so Nosto-sourced and manually-picked cards
 * render identical swatches.
 *
 * Nosto does NOT initialise on the local dev server: its script loads but `nostojs.q` never
 * drains (it validates the shop domain, and 127.0.0.1 is not it). Manual mode therefore also
 * acts as the local fallback, and Nosto mode must be verified on production.
 *
 * No module system in assets/ — attaches window.BrightonDrawerRecommendations.
 */
(function () {
  if (window.BrightonDrawerRecommendations) return;

  const NOSTO_TIMEOUT = 8000;

  const nostoApi = () =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (value) => { if (!settled) { settled = true; resolve(value); } };

      const attach = () => {
        if (typeof window.nostojs !== 'function') return false;
        window.nostojs(finish);
        return true;
      };

      if (!attach()) {
        // Poll until the async script defines nostojs, or the same timeout gives up.
        const poll = setInterval(() => {
          if (settled || attach()) clearInterval(poll);
        }, 100);
        setTimeout(() => clearInterval(poll), NOSTO_TIMEOUT);
      }

      setTimeout(() => finish(null), NOSTO_TIMEOUT);
    });

  const priceTextFor = (product) => {
    const skus = Array.isArray(product.skus) ? product.skus : [];
    const prices = skus.map((s) => Number(s.price)).filter((n) => !Number.isNaN(n) && n > 0);
    if (!prices.length) return product.price_text || '';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    // Nosto's price_text is already formatted for a single price; only build a range when the
    // SKUs genuinely differ, matching how the PDP carousel shows "$72 - $82".
    if (min === max) return product.price_text || '';
    const fmt = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
    return `${fmt(min)} - ${fmt(max)}`;
  };

  /**
   * SKU custom_fields mix product options with metadata, so options are matched on SHAPE rather
   * than by excluding known metadata keys. Shopify sends option names as their display names —
   * capitalised, spaces, no punctuation ("Color", "Ring Size") — while Nosto metadata is
   * machine-cased ("avalara-taxcode", "gtin", "skuCode", "cql-*").
   *
   * This fails closed: an unrecognised metadata field is dropped instead of being rendered to a
   * customer as a dropdown, which is what a blocklist did with avalara-taxcode.
   */
  const isOptionField = (key) => /^[A-Z][A-Za-z0-9]*( [A-Za-z0-9]+)*$/.test(String(key));
  const SWATCH_SIZE = 50;

  const swatchUrl = (raw) => {
    let url = String(raw || '');
    if (!url) return null;
    if (url.indexOf('images.salsify.com/image/') !== -1) {
      url = url.replace('images.salsify.com/image/', 'images.salsify.com/images/');
      // Segment 5 is the signature Salsify signs the baked transform with.
      const parts = url.split('/');
      url = url.replace('/upload/' + parts[5] + '/', '/w_' + SWATCH_SIZE + ',h_' + SWATCH_SIZE + '/');
      url = url.replace('.jpg', '/swatch.jpg');
    }
    return url.replace('http://', 'https://');
  };

  /** Option names whose values get rendered as swatches rather than a dropdown. */
  const SWATCH_OPTIONS = ['color', 'colour'];
  const isSwatchOption = (name) => SWATCH_OPTIONS.indexOf(String(name).toLowerCase()) !== -1;

  /**
   * Maps a Nosto product into the same shape the manual Liquid snippet emits.
   *
   * Options are derived from the SKU custom_fields so a ring renders a Ring Size dropdown
   * instead of five identical colour swatches — which is exactly what a flat variant list did.
   */
  const colourSlug = (value) => String(value || '').toLowerCase().trim().replace(/\s+/g, '-');

  /**
   * Resolves a per-colour product image.
   *
   * Nosto only populates `image_url` on the FIRST sku — every other sku's is an empty string.
   * Taken at face value that meant `sku.image_url || product.image_url` fell back to the first
   * colour's photo for all the others, so the card image never changed when switching swatch,
   * even though the swatch chips themselves were correct (those come from `cql-swatch_image`,
   * which IS populated per sku).
   *
   * The other colours' photos are present in `alternate_image_urls`. Brighton names them
   * `<handle>__<colour-slug>_<n>.jpg`, so the colour can be matched by slug. Verified against
   * the Sarai Top Handle Bag: 7 alternates, both "Luggage" and "Black" matched.
   */
  const imageForColour = (product, colour) => {
    const pool = [product.image_url, ...(product.alternate_image_urls || [])].filter(Boolean);
    if (!colour || !pool.length) return product.image_url || product.thumb_url;
    const needle = `__${colourSlug(colour)}_`;
    const matches = pool.filter((url) => String(url).toLowerCase().indexOf(needle) !== -1);
    if (!matches.length) return product.image_url || product.thumb_url;
    // Prefer the primary shot (`_0`) when a colour has several.
    return matches.find((url) => String(url).toLowerCase().indexOf(`${needle}0`) !== -1) || matches[0];
  };

  const normaliseNostoProduct = (product) => {
    const skus = Array.isArray(product.skus) ? product.skus : [];

    // Collect option names in a stable order, from the first SKU that declares each.
    const optionNames = [];
    skus.forEach((sku) => {
      Object.keys(sku.custom_fields || {}).forEach((key) => {
        if (isOptionField(key) && optionNames.indexOf(key) === -1) optionNames.push(key);
      });
    });

    const variants = skus.map((sku) => {
      const cf = sku.custom_fields || {};
      const optionValues = {};
      optionNames.forEach((name) => {
        if (cf[name] !== undefined) optionValues[name] = String(cf[name]);
      });
      // Nosto leaves image_url empty on all but the first sku, so fall back to matching the
      // colour against alternate_image_urls rather than to the product image.
      const colour = cf.Color || cf.Colour || cf['cql-generic_color'];
      return {
        id: String(sku.id),
        name: sku.name,
        available: sku.available !== false,
        image: sku.image_url || imageForColour(product, colour),
        swatch: swatchUrl(cf['cql-swatch_image']),
        optionValues,
      };
    });

    // Only keep options that actually vary — a single-colour ring shouldn't show a Color
    // control with one entry.
    const options = optionNames
      .map((name) => ({
        name,
        isSwatch: isSwatchOption(name),
        values: [...new Set(variants.map((v) => v.optionValues[name]).filter((v) => v !== undefined))],
      }))
      .filter((o) => o.values.length > 1);

    return {
      productId: String(product.product_id),
      title: product.name,
      url: product.url,
      image: product.image_url || product.thumb_url,
      priceText: priceTextFor(product),
      ratingValue: Number(product.rating_value) || 0,
      ratingCount: 0,
      options,
      variants,
    };
  };

  /**
   * Pushes the drawer's cart to Nosto so recommendations can be based on it.
   *
   * This is REQUIRED for cart-based placements to return anything real. Nosto normally reads
   * cart contents from a server-rendered `.nosto_cart` tagging block, which a drawer that
   * mutates the cart client-side never updates — so Nosto keeps seeing the cart as it was at
   * page load. Requesting `cartpage-nosto-1` without it came back with
   * `result_id: "cartpage-nosto-1-fallback-nosto-1"` and generic Best Sellers: a FALLBACK, not a
   * recommendation.
   *
   * `resendCartTagging()` takes no arguments and re-reads Nosto's own stale state, so it can't
   * help. `resendCartContent({ hcid, items })` accepts the cart directly, which is what this
   * uses.
   */
  /**
   * Nosto's `hcid` is the Shopify cart token, which the Liquid `cart` object does NOT expose —
   * it exists only on the Cart AJAX API response. Fetched here rather than carried through the
   * state contract.
   */
  const cartToken = async () => {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) return '';
      const cart = await res.json();
      return cart.token || '';
    } catch (e) {
      return '';
    }
  };

  const syncCartToNosto = async (cart) => {
    const api = await nostoApi();
    if (!api || typeof api.resendCartContent !== 'function') return false;

    const items = (cart.items || [])
      .filter((i) => !i.flags || !i.flags.hidden)
      .map((i) => ({
        product_id: String(i.productId),
        sku_id: String(i.variantId),
        quantity: Number(i.quantity) || 0,
        name: i.title,
        unit_price: (Number(i.unitPrice || 0) / 100).toFixed(2),
        price_currency_code: cart.currencyCode || 'USD',
      }));

    try {
      await api.resendCartContent({ hcid: await cartToken(), items });
      return true;
    } catch (e) {
      console.warn('[cart-drawer] could not send cart to Nosto', e && e.message);
      return false;
    }
  };

  /**
   * Fetches a Nosto placement as data. Resolves `{ products, title, resultId }`, or null when
   * Nosto is unavailable so the caller can fall back to the manual list.
   */
  const fetchNosto = async (placementId) => {
    const api = await nostoApi();
    if (!api || typeof api.createRecommendationRequest !== 'function') return null;

    try {
      const request = api.createRecommendationRequest({ includeTagging: true });
      if (typeof request.setResponseMode === 'function') request.setResponseMode('JSON_ORIGINAL');
      if (typeof request.setElements === 'function') request.setElements([placementId]);

      const response = await request.load();
      const placement = response && response.recommendations && response.recommendations[placementId];
      if (!placement || !Array.isArray(placement.products)) return null;

      return {
        products: placement.products.map(normaliseNostoProduct),
        title: placement.title || '',
        resultId: placement.result_id || '',
      };
    } catch (e) {
      console.warn('[cart-drawer] Nosto recommendations unavailable', e && e.message);
      return null;
    }
  };

  /**
   * Reports a click on a recommended product so Nosto can attribute it. Silent no-op when
   * Nosto isn't present or the products came from the manual list.
   */
  const trackClick = (resultId, productId) => {
    if (!resultId || typeof window.nostojs !== 'function') return;
    window.nostojs((api) => {
      try {
        if (typeof api.recordAttribution === 'function') {
          api.recordAttribution({ type: 'vp', target: String(productId), ref: resultId });
        }
      } catch (e) { /* attribution must never break the UI */ }
    });
  };

  /** Reports an add-to-cart originating from a recommendation. */
  const trackAddToCart = (resultId, productId) => {
    if (!resultId || typeof window.nostojs !== 'function') return;
    window.nostojs((api) => {
      try {
        if (typeof api.recommendedProductAddedToCart === 'function') {
          api.recommendedProductAddedToCart(String(productId), resultId);
        }
      } catch (e) { /* attribution must never break the UI */ }
    });
  };

  /** Whether the returned result was a Nosto fallback rather than a real recommendation. */
  const isFallbackResult = (resultId) => /fallback/i.test(String(resultId || ''));

  window.BrightonDrawerRecommendations = {
    fetchNosto,
    syncCartToNosto,
    normaliseNostoProduct,
    trackClick,
    trackAddToCart,
    isFallbackResult,
  };
})();
